/**
 * Instants du noyau (CLAUDE.md §5).
 *
 * Le noyau ne lit jamais l'horloge : un instant vient toujours d'un dataset,
 * sous forme de chaîne ISO 8601 UTC, et devient un **jour julien** — une
 * `Quantity<"time">` en jours. Le jour julien est l'échelle naturelle des
 * calculs de position solaire, et une simple soustraction y donne un intervalle.
 *
 * L'analyse syntaxique est faite à la main plutôt qu'avec `new Date` : le
 * comportement de `Date` sur les formes limites varie d'un moteur à l'autre, ce
 * qui rendrait un résultat dépendant du navigateur. C'est aussi pourquoi la
 * règle `verlet/no-nondeterminism` interdit `new Date()` dans tout le noyau.
 */

import { coreFr } from "../i18n/core-fr.js";
import { floor } from "./math.js";
import type { Quantity } from "./units.js";
import { quantity, valueIn } from "./units.js";

export class TimeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TimeError";
  }
}

/** `YYYY-MM-DDTHH:MM:SSZ` — UTC obligatoire, décalage horaire refusé. */
const ISO_UTC = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?Z$/;

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

function isLeapYear(year: number): boolean {
  if (year % 4 !== 0) return false;
  if (year % 100 !== 0) return true;
  return year % 400 === 0;
}

function daysInMonth(year: number, month: number): number {
  if (month === 2 && isLeapYear(year)) return 29;
  const days = DAYS_IN_MONTH[month - 1];
  if (days === undefined) {
    throw new TimeError(coreFr.time.monthOutOfRange(String(month)));
  }
  return days;
}

/**
 * Convertit un instant ISO 8601 UTC en jour julien.
 *
 * Algorithme grégorien standard (Fliegel–Van Flandern). Le décalage de 0,5 vient
 * de ce que le jour julien commence à midi, pas à minuit.
 */
export function julianDayFromISO(iso: string): Quantity<"time"> {
  const match = ISO_UTC.exec(iso);
  if (match === null) {
    throw new TimeError(
      coreFr.time.invalidInstant(String(iso)),
    );
  }

  const [, rawYear, rawMonth, rawDay, rawHour, rawMinute, rawSecond, rawFraction] =
    match;

  const year = Number(rawYear);
  const month = Number(rawMonth);
  const day = Number(rawDay);
  const hour = Number(rawHour);
  const minute = Number(rawMinute);
  const second = Number(rawSecond);
  const fraction = rawFraction === undefined ? 0 : Number(`0.${rawFraction}`);

  if (month < 1 || month > 12) {
    throw new TimeError(coreFr.time.invalidMonth(String(iso), String(month)));
  }
  if (day < 1 || day > daysInMonth(year, month)) {
    throw new TimeError(coreFr.time.invalidDay(String(iso), String(day)));
  }
  if (hour > 23 || minute > 59 || second > 59) {
    throw new TimeError(coreFr.time.invalidTime(String(iso)));
  }

  // Janvier et février comptent comme les mois 13 et 14 de l'année précédente,
  // ce qui place le jour bissextile en fin d'année et simplifie la formule.
  const shiftedYear = month <= 2 ? year - 1 : year;
  const shiftedMonth = month <= 2 ? month + 12 : month;

  const century = floor(shiftedYear / 100);
  const gregorian = 2 - century + floor(century / 4);

  const jdAtMidnight =
    floor(365.25 * (shiftedYear + 4716)) +
    floor(30.6001 * (shiftedMonth + 1)) +
    day +
    gregorian -
    1524.5;

  const dayFraction = (hour * 3600 + minute * 60 + second + fraction) / 86400;

  return quantity(jdAtMidnight + dayFraction, "d");
}

/** Jours écoulés depuis l'époque J2000, base des séries de position solaire. */
export function daysSinceJ2000(julianDay: Quantity<"time">): Quantity<"time"> {
  return quantity(valueIn(julianDay, "d") - 2451545.0, "d");
}
